'use client';

import ExplainDialog from '@/app/_lens-components/components/ExplainDialog';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { BasePresTypeList } from '@/app/_lens-components/types';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import { IPrescriptionOptions } from '@/components/PrescriptionFormV2/types';
import { getEmptyPrescription } from '@/components/PrescriptionFormV2/utils';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import { EPrescriptionType } from '@/utils/enums/goods';
import { processLineBreaks } from '@/utils/tools';
import { Skeleton, useMediaQuery } from '@mui/material';
import { RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useEffect, useRef, useState } from 'react';
import PresTypeDialog from './components/PresTypeDialog';
import ReadingPrescriptionPop from './components/ReadingPrescriptionPop';

interface PrescriptionTypeProps {
  presList: BasePresTypeList;
  /** 跳转下一步 */
  toNextStep: (type: EPrescriptionType, presValue?: IPrescriptionOptions) => void;
}

export default function PrescriptionType({ presList, toNextStep }: PrescriptionTypeProps) {
  const t = useTranslations('lens-process');
  const [presDialog, setPresDialog] = useState(false);
  const [showExplainPop, setShowExplainPop] = useState(false);
  const [explainContent, setExplainContent] = useState<any>({});
  const [showReadingPop, setShowReadingPop] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { trackEvent } = useReporting();
  const isWap = useMediaQuery('(max-width:1280px)');
  const waiMode = useStore((state) => state.waiMode);
  const {
    prescriptionType,
    setPresTypeOptionName,
    setPrescriptionType,
    setPrescription,
    lensGoodsSensorsData,
    setScanInfo,
  } = useLensProcessStore();

  useEffect(() => {
    setScanInfo(null);
  }, []);

  useEffect(() => {
    // 页面浏览埋点
    if (lensGoodsSensorsData) {
      setTimeout(() => {
        trackEvent(
          {
            eventName: 'PrescriptionTypePage_View',
            properties: {
              ...(lensGoodsSensorsData || {}),
            },
          },
          [ProviderType.SENSORS],
        );
      }, 500); // 延迟500ms. 1. 让相关数据就绪；2. 让 EditLensPage_View 事件先触发
    }
  }, [lensGoodsSensorsData]);

  // 处理处方类型改变
  const handlePresTypeChange = (item: (typeof presList)[0]) => {
    if (
      item.title === EPrescriptionType.READING_GLASSES &&
      [
        EPrescriptionType.READING_GLASSES,
        EPrescriptionType.READERS,
        EPrescriptionType.PRESCRIPTION_READING,
      ].includes(prescriptionType)
    ) {
      return;
    }
    setPrescriptionType(item.title as EPrescriptionType);
    if (item.title !== EPrescriptionType.READING_GLASSES) {
      toNextStep(item.title as EPrescriptionType);
    }
    if (item.title !== EPrescriptionType.READING_GLASSES) {
      // 老花镜需要进一步选择具体类型
      // 发送埋点
      trackEvent(
        {
          eventName: 'PrescriptionTypePage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type_name: item.name,
            prescription_type: item.title,
            reading_glass_type: '',
          },
        },
        [ProviderType.SENSORS],
      );
      trackEvent(
        {
          commandType: 'event',
          eventName: 'select_prescription_type',
          properties: {
            event_category: 'ADDPRESCRIPTION',
            event_label: item.title,
          },
        },
        [ProviderType.GA4],
      );
    }
  };

  // 处理老花镜类型选择
  const handleReadingPresTypeChange = (subItem: (typeof presList)[0]['items'][0]) => {
    const type = subItem.title as EPrescriptionType;
    setPrescriptionType(type);
    setPresTypeOptionName(subItem.name);
    if (type === EPrescriptionType.READERS && isWap) {
      setShowReadingPop(true);
    } else {
      toNextStep(type);
    }
    trackEvent(
      {
        eventName: 'PrescriptionTypePage_Select_Click',
        properties: {
          ...(lensGoodsSensorsData || {}),
          prescription_type_name: subItem.name,
          prescription_type: getApiPrescriptionType(prescriptionType),
          reading_glass_type: type,
        },
      },
      [ProviderType.SENSORS],
    );
    trackEvent(
      {
        commandType: 'event',
        eventName: 'select_prescription_type',
        properties: {
          event_category: 'ADDPRESCRIPTION',
          event_label: type,
        },
      },
      [ProviderType.GA4],
    );
  };

  // 显示说明弹窗
  const showExplain = (item: (typeof presList)[0]) => {
    setExplainContent(item);
    setShowExplainPop(true);
    // 发送埋点
    trackEvent(
      {
        eventName: 'PrescriptionTypePage_Introduction_Click',
        properties: {
          ...(lensGoodsSensorsData || {}),
          prescription_type_name: item.name,
          prescription_type: item.title,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const handlePresTipClick = () => {
    setPresDialog(true);
    trackEvent(
      {
        eventName: 'PrescriptionTypePage_Introduction_Click',
        properties: {
          ...(lensGoodsSensorsData || {}),
        },
      },
      [ProviderType.SENSORS],
    );
  };

  // 处理老花镜度数选择确认
  const handleReadingConfirm = (prescription: {
    od_sph: string;
    os_sph: string;
    presType: string;
  }) => {
    setShowReadingPop(false);
    // 这里可以添加处理老花镜度数选择的逻辑
    const presValue = {
      ...getEmptyPrescription(),
      od_sph: prescription.od_sph,
      os_sph: prescription.os_sph,
    };
    setPrescription(presValue);
    toNextStep(EPrescriptionType.READERS, presValue);
  };

  return (
    <div className="font-br">
      <div className={stepCommonClassName.stepTitle}>
        <span>{t('prescriptionType')}</span>
        {presList?.length > 0 && (
          <span
            className="relative cursor-pointer flex items-center hover:text-primary-purple group"
            onClick={handlePresTipClick}
          >
            <QuestionResponsive
              className={`${stepCommonClassName.questionIcon} group-hover:text-primary-purple`}
            />
            <span className="hidden xl:block text-12 ml-[6px] underline  font-br">
              {t('learnAboutDifferentLensUsages')}
            </span>
          </span>
        )}
      </div>

      <div className="flex flex-col max-xl:px-[14px] pt-1" ref={scrollRef}>
        {!presList || presList.length === 0 ? (
          // 骨架屏显示
          <>
            {[1, 2, 3].map((item) => (
              <div key={item} className={`${stepCommonClassName.prescriptionTypeOption} mb-3`}>
                <div className={stepCommonClassName.prescriptionTypeContent}>
                  <Skeleton variant="rounded" width={40} height={40} />
                  <div
                    className={`${stepCommonClassName.prescriptionTypeTextContainer} flex-1 ml-3`}
                  >
                    <div className="mb-1">
                      <Skeleton variant="text" width="60%" height={24} />
                    </div>
                    <Skeleton variant="text" width="90%" height={20} />
                  </div>
                </div>
              </div>
            ))}
          </>
        ) : (
          presList.map((item) => (
            <div
              key={item.title}
              className={`${stepCommonClassName.prescriptionTypeOption} ${
                getApiPrescriptionType(prescriptionType) === item.title
                  ? stepCommonClassName.prescriptionTypeOptionSelected
                  : ''
              }`}
              onClick={() => handlePresTypeChange(item)}
            >
              <div className={stepCommonClassName.prescriptionTypeContent}>
                <Image
                  src={item.img}
                  alt={item.name}
                  width={48}
                  height={48}
                  className={stepCommonClassName.prescriptionTypeImage}
                />
                <div className={stepCommonClassName.prescriptionTypeTextContainer}>
                  <div className={stepCommonClassName.prescriptionTypeTitle}>
                    <span className={stepCommonClassName.prescriptionTypeTitleText}>
                      {item.name}
                    </span>
                    {item.explain && (
                      <Tooltip
                        title={
                          <p
                            className="text-[14px]"
                            dangerouslySetInnerHTML={{ __html: processLineBreaks(item.explain) }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        }
                        placement="bottom"
                        variant="light"
                        sx={{
                          zIndex: 1500,
                          '& .MuiTooltip-tooltip': {
                            fontSize: '14px',
                            maxWidth: '490px',
                          },
                        }}
                      >
                        <QuestionResponsive
                          className={stepCommonClassName.questionIcon}
                          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                            isWap && showExplain(item);
                            e.stopPropagation();
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                  <span className={stepCommonClassName.prescriptionTypeDescription(waiMode)}>
                    {item.description}
                  </span>
                </div>
                {isWap && (
                  <div className={`${stepCommonClassName.prescriptionTypeArrow}`}>
                    <RightArrowOutlined
                      className={`${stepCommonClassName.arrowIcon} ${
                        getApiPrescriptionType(prescriptionType as EPrescriptionType) ===
                          EPrescriptionType.READING_GLASSES &&
                        item.title === EPrescriptionType.READING_GLASSES
                          ? stepCommonClassName.prescriptionTypeArrowRotated
                          : ''
                      }`}
                    />
                  </div>
                )}
              </div>
              {/* 老花镜子选项 */}
              {item.title === EPrescriptionType.READING_GLASSES &&
                [
                  EPrescriptionType.READING_GLASSES,
                  EPrescriptionType.READERS,
                  EPrescriptionType.PRESCRIPTION_READING,
                ].includes(prescriptionType) && (
                  <div className="transition-all duration-300">
                    {item.items?.map((subItem) => (
                      <div
                        key={subItem.title}
                        className={`${stepCommonClassName.prescriptionSubOption} ${
                          prescriptionType === subItem.title
                            ? stepCommonClassName.prescriptionSubOptionSelected
                            : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadingPresTypeChange(subItem);
                        }}
                      >
                        <div className={stepCommonClassName.prescriptionTypeTextContainer}>
                          <p className={stepCommonClassName.prescriptionSubOptionTitle}>
                            {subItem.name}
                          </p>
                          <p className={stepCommonClassName.prescriptionSubOptionDescription}>
                            {subItem.description}
                          </p>
                        </div>
                        {isWap && (
                          <div className={stepCommonClassName.prescriptionTypeArrow}>
                            <RightArrowOutlined className={stepCommonClassName.arrowIcon} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ))
        )}
      </div>

      <PresTypeDialog open={presDialog} onClose={() => setPresDialog(false)} />
      <ExplainDialog
        open={showExplainPop}
        title={explainContent.name}
        onClose={() => setShowExplainPop(false)}
        fullWidth
        className="explain-dialog"
      >
        <p
          className="text-sm whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: processLineBreaks(explainContent.explain) }}
        />
      </ExplainDialog>

      {/* 老花镜度数选择弹窗 */}
      <ReadingPrescriptionPop
        open={showReadingPop}
        onClose={() => setShowReadingPop(false)}
        onConfirm={handleReadingConfirm}
      />
    </div>
  );
}
